import { dataService } from './dataService'
import { CurrencyConversion } from '../types'

class CurrencyService {
  private defaultExchangeRates: Record<string, Record<string, number>> = {
    USD: { USD: 1.0, GBP: 0.79, INR: 83.12 },
    GBP: { USD: 1.27, GBP: 1.0, INR: 105.56 },
    INR: { USD: 0.012, GBP: 0.0095, INR: 1.0 },
  }

  private exchangeRates: Map<string, Map<string, number>> = new Map()
  private lastUpdated: string = new Date().toISOString()
  private _orgId: string | null = null
  private _isInitialized: boolean = false

  public async loadExchangeRates(orgId: string): Promise<void> {
    this._orgId = orgId
    this._isInitialized = false
    try {
      const spRates = await dataService.getExchangeRates(orgId)
      if (spRates.length === 0) {
        await this.initializeWithDefaultRates(orgId)
      } else {
        this.exchangeRates.clear()
        spRates.forEach(r => {
          if (!this.exchangeRates.has(r.from_currency)) this.exchangeRates.set(r.from_currency, new Map())
          this.exchangeRates.get(r.from_currency)!.set(r.to_currency, r.rate)
        })
        this.lastUpdated = new Date().toISOString()
      }
      this._isInitialized = true
    } catch {
      this.initializeLocalDefaults()
      this._isInitialized = true
    }
  }

  private async initializeWithDefaultRates(orgId: string): Promise<void> {
    this.initializeLocalDefaults()
    const currencies = ['USD', 'GBP', 'INR']
    for (const from of currencies) {
      for (const to of currencies) {
        if (from !== to) {
          const rate = this.defaultExchangeRates[from]?.[to]
          if (rate !== undefined) {
            await dataService.createExchangeRate(orgId, { from_currency: from as any, to_currency: to as any, rate })
          }
        }
      }
    }
  }

  private initializeLocalDefaults(): void {
    this.exchangeRates.clear()
    Object.keys(this.defaultExchangeRates).forEach(from => {
      this.exchangeRates.set(from, new Map())
      Object.keys(this.defaultExchangeRates[from]).forEach(to => {
        this.exchangeRates.get(from)!.set(to, this.defaultExchangeRates[from][to])
      })
    })
    this.lastUpdated = new Date().toISOString()
  }

  public getExchangeRate(fromCurrency: string, toCurrency: string): number {
    if (fromCurrency === toCurrency) return 1.0
    return this.exchangeRates.get(fromCurrency)?.get(toCurrency) ?? 1.0
  }

  public convert(amount: number, fromCurrency: string, toCurrency: string): CurrencyConversion {
    if (fromCurrency === toCurrency) {
      return { originalAmount: amount, originalCurrency: fromCurrency, convertedAmount: amount, convertedCurrency: toCurrency, exchangeRate: 1.0, lastUpdated: this.lastUpdated }
    }
    const rate = this.getExchangeRate(fromCurrency, toCurrency)
    return { originalAmount: amount, originalCurrency: fromCurrency, convertedAmount: amount * rate, convertedCurrency: toCurrency, exchangeRate: rate, lastUpdated: this.lastUpdated }
  }

  public getCurrencySymbol(currency: string): string {
    return { USD: '$', GBP: '£', INR: '₹' }[currency] || currency
  }

  public formatCurrency(amount: number, currency: string, decimals = 2): string {
    return `${this.getCurrencySymbol(currency)}${amount.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
  }

  public convertAndFormat(amount: number, fromCurrency: string, toCurrency: string, decimals = 2): string {
    const conversion = this.convert(amount, fromCurrency, toCurrency)
    return this.formatCurrency(conversion.convertedAmount, toCurrency, decimals)
  }

  public getSupportedCurrencies(): string[] {
    return Array.from(this.exchangeRates.keys())
  }

  public calculateTotalRevenue(revenues: Array<{ amount: number; currency: string }>, targetCurrency = 'USD'): number {
    return revenues.reduce((total, r) => total + this.convert(r.amount, r.currency, targetCurrency).convertedAmount, 0)
  }

  public getLastUpdated(): string {
    return this.lastUpdated
  }

  public getExchangeRatesMap(): Map<string, Map<string, number>> {
    return new Map(this.exchangeRates)
  }

  public async updateExchangeRate(orgId: string, fromCurrency: string, toCurrency: string, rate: number): Promise<void> {
    if (!this.exchangeRates.has(fromCurrency)) this.exchangeRates.set(fromCurrency, new Map())
    this.exchangeRates.get(fromCurrency)!.set(toCurrency, rate)
    if (!this.exchangeRates.has(toCurrency)) this.exchangeRates.set(toCurrency, new Map())
    this.exchangeRates.get(toCurrency)!.set(fromCurrency, 1 / rate)
    this.lastUpdated = new Date().toISOString()

    await dataService.createExchangeRate(orgId, { from_currency: fromCurrency as any, to_currency: toCurrency as any, rate })
    await dataService.createExchangeRate(orgId, { from_currency: toCurrency as any, to_currency: fromCurrency as any, rate: 1 / rate })
  }
}

export const currencyService = new CurrencyService()
